import { useState } from "react";
import { ChevronLeft, Trash2, Save } from "lucide-react";
import { C, CRED_TYPES, sBtn, sFull, sPill, sTopBar } from "../styles";

export default function EditEntry({ entry, onSave, onCancel, onDelete }) {
    const initialType = entry
        ? CRED_TYPES.find((t) => t.id === entry.type) || CRED_TYPES[0]
        : CRED_TYPES[0];
    const [selType, setSelType] = useState(initialType);
    const [form, setForm] = useState(() => {
        const f = {};
        initialType.fields.forEach((fld) => (f[fld] = entry?.fields?.[fld] || ""));
        return f;
    });
    const [saving, setSaving] = useState(false);

    const onTypeChange = (t) => {
        if (entry) return; // locked once created
        setSelType(t);
        const f = {};
        t.fields.forEach((fld) => (f[fld] = ""));
        setForm(f);
    };

    const submit = async () => {
        const firstField = selType.fields[0];
        if (!form[firstField]?.trim()) return;
        setSaving(true);
        await onSave(selType.id, form);
        setSaving(false);
    };

    return (
        <div style={sFull} data-testid="edit-screen">
            <div style={sTopBar}>
                <button
                    onClick={onCancel}
                    style={iconBtn}
                    data-testid="edit-back-btn"
                >
                    <ChevronLeft size={16} />
                </button>
                <div
                    style={{
                        fontSize: 12,
                        color: C.muted,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                    }}
                >
                    {entry ? "Edit" : "New entry"}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    {entry && (
                        <button
                            onClick={onDelete}
                            style={{
                                ...iconBtn,
                                color: C.danger,
                                borderColor: "rgba(217,43,43,0.3)",
                            }}
                            data-testid="edit-delete-btn"
                        >
                            <Trash2 size={15} />
                        </button>
                    )}
                    <button
                        onClick={submit}
                        disabled={saving || !form[selType.fields[0]]?.trim()}
                        style={{
                            ...iconBtn,
                            background: `${C.accent}22`,
                            color: C.accent,
                            borderColor: `${C.accent}55`,
                            opacity:
                                saving || !form[selType.fields[0]]?.trim()
                                    ? 0.4
                                    : 1,
                        }}
                        data-testid="edit-save-btn"
                    >
                        <Save size={14} style={{ marginRight: 6 }} />
                        Save
                    </button>
                </div>
            </div>

            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "20px 20px 60px",
                    maxWidth: 520,
                    width: "100%",
                    margin: "0 auto",
                }}
            >
                {!entry && (
                    <>
                        <div style={sectionLabel}>TYPE</div>
                        <div style={{ marginBottom: 20 }} data-testid="edit-type-selector">
                            {CRED_TYPES.map((t) => (
                                <span
                                    key={t.id}
                                    style={sPill(selType.id === t.id, t.color)}
                                    onClick={() => onTypeChange(t)}
                                    data-testid={`edit-type-${t.id}`}
                                >
                                    {t.label}
                                </span>
                            ))}
                        </div>
                    </>
                )}

                {selType.fields.map((fld) => (
                    <div key={fld} style={{ marginBottom: 14 }}>
                        <div style={sectionLabel}>{fld.toUpperCase()}</div>
                        {fld === "Content" ? (
                            <textarea
                                value={form[fld] || ""}
                                onChange={(e) =>
                                    setForm({ ...form, [fld]: e.target.value })
                                }
                                placeholder={`Enter ${fld.toLowerCase()}…`}
                                rows={8}
                                style={{
                                    ...inpStyle,
                                    minHeight: 180,
                                    resize: "vertical",
                                    lineHeight: 1.6,
                                }}
                                data-testid={`edit-field-${fld}`}
                            />
                        ) : fld === "Expiry" ? (
                            <input
                                type="date"
                                value={form[fld] || ""}
                                onChange={(e) =>
                                    setForm({ ...form, [fld]: e.target.value })
                                }
                                style={inpStyle}
                                data-testid={`edit-field-${fld}`}
                            />
                        ) : (
                            <input
                                type={
                                    fld === "Password" || fld === "Key"
                                        ? "password"
                                        : "text"
                                }
                                value={form[fld] || ""}
                                onChange={(e) =>
                                    setForm({ ...form, [fld]: e.target.value })
                                }
                                placeholder={`Enter ${fld.toLowerCase()}…`}
                                style={inpStyle}
                                data-testid={`edit-field-${fld}`}
                            />
                        )}
                    </div>
                ))}

                <button
                    onClick={submit}
                    style={{ ...sBtn("primary"), marginTop: 20 }}
                    disabled={saving || !form[selType.fields[0]]?.trim()}
                    data-testid="edit-save-bottom-btn"
                >
                    {saving ? "Saving…" : entry ? "Update entry" : "Create entry"}
                </button>
            </div>
        </div>
    );
}

const iconBtn = {
    background: "transparent",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    color: C.muted,
    padding: "7px 12px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    fontFamily: C.font,
    fontSize: 12,
};

const sectionLabel = {
    fontSize: 10,
    letterSpacing: "0.2em",
    color: C.dim,
    marginBottom: 8,
};

const inpStyle = {
    width: "100%",
    padding: "12px 14px",
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    color: C.text,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: C.font,
};
